import { FilesIcon } from "@phosphor-icons/react/dist/csr/Files";
import { useSearchParams } from "react-router-dom";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@volca/ui/components/ui/empty";
import { Stack } from "@volca/ui/components/ui/stack";
import type { RecordObject } from "@/profitability/records/objects";
import type { ProfitabilityRun, ReportOutput } from "@/profitability/types";
import { CITATION_PARAMS } from "./citation";
import {
  type DocumentSlot,
  documentEntries,
  type RunDocuments,
  selectedEntry,
} from "./document-entries";
import { DocumentList } from "./document-list";
import { DocumentPane } from "./document-pane";
import type { DocumentLinks } from "./links";

export type DocumentsRun = RunDocuments &
  Pick<ProfitabilityRun, "id" | "start_date" | "end_date" | "matched_columns">;

/**
 * The period's documents, the way a file browser shows them: on the left
 * every file the period read, in the order the template reads them (an input
 * with nothing uploaded says so); on the right the one selected (`?doc=`, so
 * a link opens it, `?view=` the way it is shown and the citation params the
 * place in it, ./citation): the file as uploaded, what was read from it,
 * what in the report it fed, and its details. Stacks on a narrow screen.
 */
export const DocumentsTab = ({
  businessId,
  run,
  slots,
  output,
  objects = [],
  links,
  canUpload,
  onAsk,
}: {
  businessId: string;
  run: DocumentsRun;
  /** The template's inputs, in its order. */
  slots: DocumentSlot[];
  /** The period's report, which says which columns came from which input. */
  output?: ReportOutput;
  objects?: RecordObject[];
  links: DocumentLinks;
  /** Only someone who may run the template is offered an upload. */
  canUpload: boolean;
  /** Opens the page's chat with a question; absent where there is none. */
  onAsk?: (question: string) => void;
}) => {
  const [params, setParams] = useSearchParams();
  const entries = documentEntries(slots, run);
  const selected = selectedEntry(entries, params.get("doc"));
  const select = (documentId: string) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set("doc", documentId);
        // A citation points into the document it came with.
        for (const key of CITATION_PARAMS) next.delete(key);
        return next;
      },
      { replace: true },
    );

  if (!entries.some((entry) => entry.document))
    return (
      <Empty className="border border-dashed border-line">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <FilesIcon aria-hidden />
          </EmptyMedia>
          <EmptyTitle>No documents for this period</EmptyTitle>
          <EmptyDescription>
            The files a period is built from are listed here once they are
            uploaded.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );

  return (
    <Stack
      gap={6}
      align="stretch"
      className="w-full min-w-0 lg:min-h-0 lg:flex-1 lg:flex-row"
    >
      <DocumentList
        entries={entries}
        selectedId={selected?.document?.id ?? null}
        onSelect={select}
        uploadHref={canUpload ? links.upload : undefined}
        className="lg:w-72 lg:shrink-0 lg:overflow-y-auto"
      />
      {selected?.document && (
        <DocumentPane
          key={selected.document.id}
          businessId={businessId}
          run={run}
          entry={selected}
          document={selected.document}
          output={output}
          objects={objects}
          links={links}
          canUpload={canUpload}
          onAsk={onAsk}
        />
      )}
    </Stack>
  );
};
